import React, { FC, useCallback, useEffect } from "react";
import {
  ContentWrapper,
  Heading,
} from "containers/RightPanel/RightPanel.styles";
import { ButtonColor, ButtonType, ButtonVariant } from "enums/Button";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { createFieldBlurHandler } from "utils/formValidation";
import { connect } from "react-redux";
import {
  ButtonsWrapper,
  Content,
  FormWrapper,
} from "../../../../components/Addresses/components/AddressForm.styles";
import Button from "../../../../components/Button/Button";
import Input from "../../../../components/Input/Input";
import {
  createShopifyProfile,
  updateShopifyProfile,
} from "../../../../reduxFolder/reducers/shopifyProfiles";
import { closeRightPanel } from "../../../../reduxFolder/reducers/rightPanel";
import {
  shopifyProfileFormHelper,
  ShopifyProfileFormValues,
} from "../../helpers/shopifyProfileFormHelper";

interface ShopifyProfileFormPanelProps {
  panelData: any;
  isOpen: boolean;
  closeRightPanel: () => void;
  createShopifyProfile: (data: any) => Promise<any>;
  updateShopifyProfile: (id: number, data: any) => Promise<any>;
}

const ShopifyProfileFormPanel: FC<ShopifyProfileFormPanelProps> = ({
  panelData,
  isOpen,
  closeRightPanel,
  createShopifyProfile,
  updateShopifyProfile,
}) => {
  const { t } = useTranslation("common");
  const existingProfile = panelData.get("profile");
  const isEditMode = !!existingProfile;

  const submitAction = useCallback(
    (data: any) => {
      if (isEditMode) {
        return updateShopifyProfile(existingProfile.get("id"), data);
      }
      return createShopifyProfile(data);
    },
    [isEditMode, existingProfile, createShopifyProfile, updateShopifyProfile],
  );

  const initialValues: ShopifyProfileFormValues | undefined = isEditMode
    ? {
        store_name: existingProfile.get("store_name") || "",
        api_secret_key: existingProfile.get("api_secret_key") || "",
        admin_api_access_token:
          existingProfile.get("admin_api_access_token") || "",
        location_id: existingProfile.get("location_id") || "",
      }
    : undefined;

  const formProps = useFormik(
    shopifyProfileFormHelper(t, submitAction, closeRightPanel, initialValues),
  );

  const {
    values,
    errors,
    touched,
    handleChange,
    handleSubmit,
    handleReset,
    isSubmitting,
  } = formProps;
  const handleBlur = createFieldBlurHandler(formProps);

  useEffect(() => {
    if (!isOpen) handleReset(null);
  }, [isOpen]);

  return (
    <ContentWrapper>
      <Heading>
        {isEditMode ? t("shopify.editProfile") : t("shopify.createProfile")}
      </Heading>
      <FormWrapper onSubmit={handleSubmit}>
        <Content>
          <Input
            placeholder={t("shopify.storeName")}
            name="store_name"
            value={values.store_name}
            onChange={handleChange}
            onBlur={handleBlur}
            tooltip={touched.store_name ? (errors.store_name as string) : ""}
            invalid={touched.store_name && !!errors.store_name}
          />
          <Input
            placeholder={t("shopify.apiSecretKey")}
            name="api_secret_key"
            value={values.api_secret_key}
            onChange={handleChange}
            onBlur={handleBlur}
            tooltip={
              touched.api_secret_key ? (errors.api_secret_key as string) : ""
            }
            invalid={touched.api_secret_key && !!errors.api_secret_key}
          />
          <Input
            placeholder={t("shopify.adminApiAccessToken")}
            name="admin_api_access_token"
            value={values.admin_api_access_token}
            onChange={handleChange}
            onBlur={handleBlur}
            tooltip={
              touched.admin_api_access_token
                ? (errors.admin_api_access_token as string)
                : ""
            }
            invalid={
              touched.admin_api_access_token &&
              !!errors.admin_api_access_token
            }
          />
          <Input
            placeholder={t("shopify.locationId")}
            name="location_id"
            value={values.location_id}
            onChange={handleChange}
            onBlur={handleBlur}
            tooltip={
              touched.location_id ? (errors.location_id as string) : ""
            }
            invalid={touched.location_id && !!errors.location_id}
          />
        </Content>
        <ButtonsWrapper>
          <Button onClick={closeRightPanel}>{t("common.cancel")}</Button>
          <Button
            isLoading={isSubmitting}
            disabled={isSubmitting}
            type={ButtonType.Submit}
            color={ButtonColor.Primary}
            variant={ButtonVariant.Filled}
          >
            {isEditMode ? t("shopify.editProfile") : t("shopify.createProfile")}
          </Button>
        </ButtonsWrapper>
      </FormWrapper>
    </ContentWrapper>
  );
};

const withConnect = connect(null, {
  createShopifyProfile,
  updateShopifyProfile,
  closeRightPanel,
});

export default withConnect(ShopifyProfileFormPanel);
